import {AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MatPaginator, PageEvent} from "@angular/material/paginator";
import {NgForm} from "@angular/forms";
import {Terminals} from "../../../../shared/interface/terminals";
import {ReplaySubject} from "rxjs";
import {UserPermissions} from "../../../../shared/interface/user-permissions";
import {ReceivablesTransfer} from "../../../../shared/interface/receivables-transfer";
import {MatDialog} from "@angular/material/dialog";
import {AuthService} from "../../../../shared/services/auth.service";
import {select, Store} from "@ngrx/store";
import {StoreRootState} from "../../../../shared/ngrx/reducers";
import {FilterService} from "../../../../shared/services/filter.service";
import {MatSnackBar} from "@angular/material/snack-bar";
import {ActivatedRoute, Router} from "@angular/router";
import {delay, distinctUntilChanged, first, takeUntil, tap} from "rxjs/operators";
import {CountedAndPaginated} from "../../../../shared/interface/response/counted-and-paginated";
import {ReceivablesTransferService} from "../../../../shared/services/receivables-transfer.service";
import {getCurrentRouteState} from "../../../../shared/ngrx/selector";
import {RouterStateUrl} from "../../../../shared/ngrx/route-serializer";
import {ConfirmDialogComponent} from "../../../../shared/components/confirm-dialog/confirm-dialog.component";
import {Sort} from "@angular/material/sort";
import {JobResultDialogComponent} from "../../../../shared/components/job-result-dialog/job-result-dialog.component";
import {HttpErrorResponse} from "@angular/common/http";
import {Uuid} from "../../../../shared/interface/response/uuid";
import {ReceivablesBankJobService} from "../../../../shared/services/receivables-bank-job.service";

@Component({
  selector: 'app-transfer-list',
  templateUrl: './transfer-list.component.html',
  styleUrls: ['./transfer-list.component.scss']
})
export class TransferListComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild('filtersForm') filtersForm: NgForm;
  @Input() statusKey?: string[] = null;
  @Input() generate = null;


  totalData = 0;
  totalDataAll = 0;
  sortBy = 'dateAdded';
  sortOrder = 'desc';
  displayedColumns: string[] = ['name' , 'transferTitle' , 'accountNumber'  , 'sum' ,  'status' , 'createAd'];
  receivablesTransfers: ReceivablesTransfer[] = [];
  userPermissions: ReplaySubject<UserPermissions>;
  initialFilters: any;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private dialog: MatDialog,
    private receivablesTransferService: ReceivablesTransferService,
    private auth: AuthService,
    private store: Store<StoreRootState>,
    private filterService: FilterService,
    private snack: MatSnackBar,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private receivablesBankJobService: ReceivablesBankJobService,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.receivablesTransferService.getReceivablesTransferListSubject()
      .pipe(
        takeUntil(this.destroyed$),
        tap((e: CountedAndPaginated<ReceivablesTransfer>) => {
          this.receivablesTransfers = e.items;
          this.totalData = e.pagination.total;
          if (this.totalDataAll === 0 && this.generate !== null) {
            this.totalDataAll = this.totalData;
          }
        })
      ).subscribe();
    this.parseFilters();
  }

  ngOnInit(): void {
    this.parseFilters();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }


  ngAfterViewInit(): void {
    this.resetFilters();
    this.activatedRoute.data
      .pipe(
        delay(0),
        first()
      )
      .subscribe((data: any) => {
        this.statusKey = this.statusKey ?? data.statusKey ?? null;
        this.generate = this.generate ?? data.generate ?? null;
        this.store
          .pipe(
            select(getCurrentRouteState),
            delay(0),
            distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
            takeUntil(this.destroyed$),
          )
          .subscribe((state: RouterStateUrl) => {
            this.parseFilters(state.params);
            this.paginator.pageIndex = state.params.page;
            this.paginator.pageSize = state.params.pageSize;
            this.loadListData();
          })
        ;
      });

    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((event: PageEvent) => {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
        page: event.pageIndex,
        pageSize: event.pageSize,
        ...this.filtersForm.value
      })]);
    });
  }

  generateFile(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz wygenerować plik do banku?'},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {

        this.receivablesBankJobService.generate()
          .subscribe(() => {
            this.router.navigate(['/naleznosci/przelewy/pliki/lista']);
          });
      }
    });

  }

  sortChanged($event: Sort): void {
    this.sortBy = $event.active;
    this.sortOrder = $event.direction;
    this.loadListData();
  }

  loadListData(): void {
    this.receivablesTransferService.loadList(this.buildFilters(),
      this.paginator.pageIndex, this.paginator.pageSize,
      {
        sortBy: this.sortBy,
        sortOrder: this.sortOrder
      }
    );

  }

  trackTask(index: number, receivablesTransfer: ReceivablesTransfer): string {
    return receivablesTransfer.uuid;
  }


  filter(): void {
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
      page: 0,
      pageSize: this.paginator.pageSize,
      ...this.filtersForm.value,
    })]);
  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      lte$transferAmount: 'string',
      gte$transferAmount: 'string',
      contains$name: 'string',
      contains$accountNumber: 'string',
      contains$transferTitle: 'string',
    }, filters);
  }

  buildFilters(): any {
    const filters = {...this.initialFilters};

    if (null !== this.statusKey) {
      filters.statusKey = this.statusKey;
    }

    return filters;
  }

  downloadReport(): void {
    this.receivablesTransferService.downloadReport(this.buildFilters())
      .subscribe((response: Uuid) => {
        const dialogRef = this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

  resetFilters(): void {
    this.filtersForm.resetForm();
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, {
      page: 0,
      pageSize: this.paginator.pageSize,
    }]);
  }
}
